import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { finalize } from 'rxjs';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { showFallbackHttpError } from 'src/app/shared/utils/http-error-toast.util';
import { ConstantUtil } from 'src/app/util/constant.util';

type PointAuditSceneKey = 'AdminGrant' | 'OrderPaid' | 'QuotaDeduct' | 'OrderCompensation' | 'OrderRefund' | 'Other';
type PointAuditStatusKey = 'success' | 'processing' | 'failed' | 'booked' | 'pending' | 'skipped' | 'unknown';

interface PointAuditRecord {
  id: string;
  eventId: string;
  time: number | null;
  tenantName: string;
  tenantId: string;
  openId: string;
  sceneKey: PointAuditSceneKey;
  sceneLabel: string;
  statusKey: PointAuditStatusKey;
  statusLabel: string;
  reason: string;
  amount: number | null;
  aipoolTrace: string;
  operatorName: string;
  sourceBizId: string;
  raw: Record<string, unknown>;
}

interface PointAuditSummary {
  grantTotal: number;
  deductTotal: number;
  grantCount: number;
  abnormalCount: number;
}

interface PointAuditPage {
  items: PointAuditRecord[];
  total: number;
  pageIndex: number;
  pageSize: number;
  hasNextPage: boolean;
  summary: PointAuditSummary;
}

interface PointAuditMetric {
  key: 'grant' | 'deduct' | 'count' | 'abnormal';
  labelKey: string;
  value: string;
  tone: 'green' | 'red' | 'blue' | 'orange';
}

interface SelectOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-point-audit-center',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    I18nPipe,
    NzButtonModule,
    NzCardModule,
    NzDrawerModule,
    NzEmptyModule,
    NzInputModule,
    NzPaginationModule,
    NzSelectModule,
    NzSpinModule,
    NzTagModule,
    TestIdDirective
  ],
  templateUrl: './point-audit-center.component.html',
  styleUrls: ['./point-audit-center.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PointAuditCenterComponent implements OnInit {
  private readonly http = inject(HttpService);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  private readonly message = inject(NzMessageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly numberFormatterCache = new Map<string, Intl.NumberFormat>();
  private readonly dateTimeFormatterCache = new Map<string, Intl.DateTimeFormat>();

  loading = false;
  recordsLoading = false;
  searchKeyword = '';
  selectedScene = '';
  selectedStatus = '';
  operatorKeyword = '';
  sourceBizIdKeyword = '';
  pageIndex = 1;
  pageSize = 10;
  total = 0;
  hasNextPage = false;
  refreshTime = Date.now();
  metrics: PointAuditMetric[] = [];
  records: PointAuditRecord[] = [];
  selectedRecord: PointAuditRecord | null = null;
  detailVisible = false;

  readonly sceneOptions: SelectOption[] = [
    { label: this.t('point.audit.scene.admin-grant'), value: 'AdminGrant' },
    { label: this.t('point.audit.scene.order-paid'), value: 'OrderPaid' },
    { label: this.t('point.audit.scene.quota-deduct'), value: 'QuotaDeduct' },
    { label: this.t('point.audit.scene.order-compensation'), value: 'OrderCompensation' },
    { label: this.t('point.audit.scene.order-refund'), value: 'OrderRefund' }
  ];

  readonly statusOptions: SelectOption[] = [
    { label: this.t('point.audit.status.success'), value: 'Success' },
    { label: this.t('point.audit.status.processing'), value: 'Processing' },
    { label: this.t('point.audit.status.failed'), value: 'Failed' },
    { label: this.t('point.audit.status.booked'), value: 'Booked' },
    { label: this.t('point.audit.status.pending'), value: 'Pending' },
    { label: this.t('point.audit.status.skipped'), value: 'Skipped' }
  ];

  ngOnInit(): void {
    this.loadData();
  }

  t(key: string, params?: Record<string, unknown>): string {
    return this.i18n.fanyi(key, params);
  }

  reload(): void {
    this.refreshTime = Date.now();
    this.loadData();
  }

  search(): void {
    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.loadData();
  }

  onSceneFilterChange(value: string | null): void {
    this.selectedScene = value ?? '';
    this.search();
  }

  onStatusFilterChange(value: string | null): void {
    this.selectedStatus = value ?? '';
    this.search();
  }

  resetFilters(): void {
    this.searchKeyword = '';
    this.selectedScene = '';
    this.selectedStatus = '';
    this.operatorKeyword = '';
    this.sourceBizIdKeyword = '';
    this.search();
  }

  onPageIndexChange(page: number): void {
    this.pageIndex = page;
    this.loadData(true);
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.loadData(true);
  }

  openDetail(record: PointAuditRecord): void {
    this.selectedRecord = record;
    this.detailVisible = true;
  }

  closeDetail(): void {
    this.detailVisible = false;
    this.selectedRecord = null;
  }

  getAmountClass(amount: number | null): string {
    if ((amount ?? 0) > 0) {
      return 'is-positive';
    }
    if ((amount ?? 0) < 0) {
      return 'is-negative';
    }
    return '';
  }

  getStatusClass(statusKey: PointAuditStatusKey): string {
    switch (statusKey) {
      case 'success':
        return 'audit-status-tag--success';
      case 'processing':
      case 'pending':
        return 'audit-status-tag--processing';
      case 'failed':
        return 'audit-status-tag--failed';
      case 'booked':
        return 'audit-status-tag--booked';
      case 'skipped':
        return 'audit-status-tag--skipped';
      default:
        return 'audit-status-tag--default';
    }
  }

  formatNumber(value: number | null | undefined): string {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return this.getEmptyText();
    }
    return this.getNumberFormatter().format(Number(value));
  }

  formatSignedNumber(value: number | null | undefined): string {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return this.getEmptyText();
    }
    const numberValue = Number(value);
    const prefix = numberValue > 0 ? '+' : '';
    return `${prefix}${this.formatNumber(numberValue)}`;
  }

  formatDateTime(value: number | null | undefined): string {
    if (!value) {
      return this.getEmptyText();
    }
    return this.getDateTimeFormatter().format(new Date(value));
  }

  objectEntries(record: Record<string, unknown> | undefined): Array<{ key: string; value: string }> {
    return Object.entries(record ?? {}).map(([key, value]) => ({
      key,
      value: this.stringifyValue(value)
    }));
  }

  private loadData(recordsOnly = false): void {
    if (recordsOnly) {
      this.recordsLoading = true;
    } else {
      this.loading = true;
    }

    this.http
      .post(ConstantUtil.ADMIN_POINT_AUDIT_V1_LIST, this.buildPayload())
      .pipe(
        finalize(() => {
          this.loading = false;
          this.recordsLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: response => {
          const page = this.extractPage(response);
          this.records = page.items;
          this.total = page.total;
          this.pageIndex = Math.max(page.pageIndex + 1, 1);
          this.pageSize = Math.max(page.pageSize, 1);
          this.hasNextPage = page.hasNextPage;
          this.metrics = this.buildMetrics(page.summary);
        },
        error: err => {
          showFallbackHttpError(this.message, err?.error?.msg, this.t('point.audit.load-failed'));
        }
      });
  }

  private buildPayload(): Record<string, unknown> {
    return {
      key: this.searchKeyword.trim() || undefined,
      auditScene: this.selectedScene || undefined,
      status: this.selectedStatus || undefined,
      operatorId: this.operatorKeyword.trim() || undefined,
      sourceBizId: this.sourceBizIdKeyword.trim() || undefined,
      pageIndex: Math.max(this.pageIndex - 1, 0),
      pageSize: this.pageSize,
      direction: 'desc',
      orderBy: 'occurredAt',
      refreshTime: this.refreshTime
    };
  }

  private extractPage(response: unknown): PointAuditPage {
    const result = (response ?? {}) as Record<string, unknown>;
    const isSuccess = !!result['success'] || result['status'] === 0 || result['status'] === 200;
    if (!isSuccess) {
      return this.buildEmptyPage();
    }

    const data = this.pickObject(result['data']) ?? result;
    const items = this.pickArray(data['items']) ?? this.pickArray(data['records']) ?? this.pickArray(data['list']) ?? [];
    const normalizedItems = items
      .map(item => this.normalizeRecord(item))
      .sort((a, b) => {
        const timeDiff = (b.time ?? 0) - (a.time ?? 0);
        if (timeDiff !== 0) {
          return timeDiff;
        }
        return String(b.eventId ?? '').localeCompare(String(a.eventId ?? ''));
      });
    const total = this.toNumber(data['totalCount'] ?? data['total'] ?? data['count']) ?? normalizedItems.length;
    const pageIndex = this.toNumber(data['pageIndex'] ?? data['page']) ?? Math.max(this.pageIndex - 1, 0);
    const pageSize = this.toNumber(data['pageSize']) ?? this.pageSize;
    const hasNextPage = this.toBoolean(data['hasNextPage']) || (pageIndex + 1) * pageSize < total;

    return {
      items: normalizedItems,
      total,
      pageIndex,
      pageSize,
      hasNextPage,
      summary: this.extractSummary(data, normalizedItems)
    };
  }

  private buildEmptyPage(): PointAuditPage {
    return {
      items: [],
      total: 0,
      pageIndex: Math.max(this.pageIndex - 1, 0),
      pageSize: this.pageSize,
      hasNextPage: false,
      summary: {
        grantTotal: 0,
        deductTotal: 0,
        grantCount: 0,
        abnormalCount: 0
      }
    };
  }

  private normalizeRecord(item: unknown): PointAuditRecord {
    const raw = this.pickObject(item) ?? {};
    const emptyText = this.getEmptyText();
    const eventId =
      this.toString(raw['eventId']) ??
      this.toString(raw['id']) ??
      this.toString(raw['auditId']) ??
      this.toString(raw['sourceEventId']) ??
      emptyText;
    const sceneKey = this.resolveSceneKey(raw);
    const statusKey = this.resolveStatusKey(raw);
    const amount = this.resolveAmount(raw);

    return {
      id: this.toString(raw['id']) ?? eventId,
      eventId,
      time: this.toNumber(raw['occurredAt'] ?? raw['time'] ?? raw['createTime'] ?? raw['occurTime']),
      tenantName: this.toString(raw['tenantName']) ?? emptyText,
      tenantId: this.toString(raw['tenantId']) ?? emptyText,
      openId: this.toString(raw['openId']) ?? emptyText,
      sceneKey,
      sceneLabel: this.toString(raw['auditSceneText']) ?? this.getSceneLabel(sceneKey),
      statusKey,
      statusLabel: this.toString(raw['statusText']) ?? this.getStatusLabel(statusKey),
      reason: this.toString(raw['reason'] ?? raw['remark'] ?? raw['description'] ?? raw['note'] ?? raw['failReason']) ?? emptyText,
      amount,
      aipoolTrace: this.toString(raw['aipoolTrace']) ?? emptyText,
      operatorName: this.toString(raw['operatorName'] ?? raw['operatorId']) ?? emptyText,
      sourceBizId: this.toString(raw['sourceBizId']) ?? emptyText,
      raw
    };
  }

  private extractSummary(data: Record<string, unknown>, items: PointAuditRecord[]): PointAuditSummary {
    const summary = this.pickObject(data['summary']);
    const grantTotal =
      this.toNumber(summary?.['grantTotalPointCount'] ?? data['grantTotalPointCount'] ?? data['grantTotal'] ?? data['incomeTotal']) ??
      items.filter(item => item.amount !== null && item.amount > 0).reduce((sum, item) => sum + (item.amount ?? 0), 0);
    const deductTotal =
      this.toNumber(summary?.['deductTotalPointCount'] ?? data['deductTotalPointCount'] ?? data['deductTotal'] ?? data['expenseTotal']) ??
      Math.abs(items.filter(item => item.amount !== null && item.amount < 0).reduce((sum, item) => sum + (item.amount ?? 0), 0));
    const grantCount =
      this.toNumber(summary?.['grantRecordCount'] ?? data['grantRecordCount'] ?? data['grantCount']) ??
      items.filter(item => item.sceneKey === 'AdminGrant' && (item.amount ?? 0) > 0).length;
    const abnormalCount =
      this.toNumber(summary?.['anomalyRecordCount'] ?? data['anomalyRecordCount'] ?? data['abnormalCount']) ??
      items.filter(item => item.statusKey === 'failed' || item.statusKey === 'processing' || item.statusKey === 'pending').length;

    return {
      grantTotal,
      deductTotal,
      grantCount,
      abnormalCount
    };
  }

  private buildMetrics(summary: PointAuditSummary): PointAuditMetric[] {
    return [
      {
        key: 'grant',
        labelKey: 'point.audit.metric.grant-total',
        value: this.formatNumber(summary.grantTotal),
        tone: 'green'
      },
      {
        key: 'deduct',
        labelKey: 'point.audit.metric.deduct-total',
        value: this.formatNumber(summary.deductTotal),
        tone: 'red'
      },
      {
        key: 'count',
        labelKey: 'point.audit.metric.grant-count',
        value: this.formatNumber(summary.grantCount),
        tone: 'blue'
      },
      {
        key: 'abnormal',
        labelKey: 'point.audit.metric.abnormal-count',
        value: this.formatNumber(summary.abnormalCount),
        tone: 'orange'
      }
    ];
  }

  private resolveSceneKey(raw: Record<string, unknown>): PointAuditSceneKey {
    const scene = String(raw['auditScene'] ?? raw['type'] ?? raw['scene'] ?? raw['category'] ?? '')
      .trim()
      .toLowerCase();
    if (scene === 'quotadeduct') {
      return 'QuotaDeduct';
    }
    if (scene === 'orderpaid') {
      return 'OrderPaid';
    }
    if (scene === 'ordercompensation') {
      return 'OrderCompensation';
    }
    if (scene === 'orderrefund') {
      return 'OrderRefund';
    }
    if (scene === 'admingrant' || scene.includes('grant')) {
      return 'AdminGrant';
    }
    return 'Other';
  }

  private resolveStatusKey(raw: Record<string, unknown>): PointAuditStatusKey {
    const rawStatus = String(
      raw['status'] ??
        raw['statusText'] ??
        raw['statusLabel'] ??
        raw['deductStatus'] ??
        raw['grantStatus'] ??
        raw['tradeStatus'] ??
        raw['aipoolResponseCode'] ??
        ''
    ).trim();
    const status = rawStatus.toLowerCase();

    if (status === 'success' || rawStatus.includes('成功')) {
      return 'success';
    }
    if (status === 'processing' || rawStatus.includes('处理中') || rawStatus.includes('處理中')) {
      return 'processing';
    }
    if (status === 'pending' || rawStatus.includes('待处理') || rawStatus.includes('待處理')) {
      return 'pending';
    }
    if (status === 'skipped' || rawStatus.includes('跳过') || rawStatus.includes('跳過')) {
      return 'skipped';
    }
    if (status === 'booked' || rawStatus.includes('挂账') || rawStatus.includes('掛賬')) {
      return 'booked';
    }
    if (status === 'failed' || status === 'error' || rawStatus.includes('失败') || rawStatus.includes('失敗')) {
      return 'failed';
    }

    return 'unknown';
  }

  private resolveAmount(raw: Record<string, unknown>): number | null {
    const pointChangeAmount = this.toNumber(raw['pointChange'] ?? raw['pointChangeAmount']);
    if (pointChangeAmount !== null) {
      return pointChangeAmount;
    }

    const ailePointsChange = this.toNumber(raw['ailePointsChange']) ?? 0;
    const uuponPointsChange = this.toNumber(raw['uuponPointsChange']) ?? 0;
    if (ailePointsChange || uuponPointsChange) {
      return ailePointsChange + uuponPointsChange;
    }

    const grantAmount = this.toNumber(raw['grantAmount'] ?? raw['amount']);
    if (grantAmount !== null && this.resolveSceneKey(raw) === 'AdminGrant') {
      return Math.abs(grantAmount);
    }

    const deductAmount = this.toNumber(raw['deductAmount']);
    if (deductAmount !== null) {
      return -Math.abs(deductAmount);
    }

    return null;
  }

  private getSceneLabel(sceneKey: PointAuditSceneKey): string {
    switch (sceneKey) {
      case 'AdminGrant':
        return this.t('point.audit.scene.admin-grant');
      case 'OrderPaid':
        return this.t('point.audit.scene.order-paid');
      case 'QuotaDeduct':
        return this.t('point.audit.scene.quota-deduct');
      case 'OrderCompensation':
        return this.t('point.audit.scene.order-compensation');
      case 'OrderRefund':
        return this.t('point.audit.scene.order-refund');
      default:
        return this.t('point.audit.scene.other');
    }
  }

  private getStatusLabel(statusKey: PointAuditStatusKey): string {
    switch (statusKey) {
      case 'success':
        return this.t('point.audit.status.success');
      case 'processing':
        return this.t('point.audit.status.processing');
      case 'pending':
        return this.t('point.audit.status.pending');
      case 'failed':
        return this.t('point.audit.status.failed');
      case 'booked':
        return this.t('point.audit.status.booked');
      case 'skipped':
        return this.t('point.audit.status.skipped');
      default:
        return this.t('point.audit.status.unknown');
    }
  }

  private pickObject(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
  }

  private pickArray(value: unknown): unknown[] | null {
    return Array.isArray(value) ? value : null;
  }

  private toString(value: unknown): string | null {
    const normalized = String(value ?? '').trim();
    return normalized ? normalized : null;
  }

  private toNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const normalized = Number(value);
    return Number.isFinite(normalized) ? normalized : null;
  }

  private toBoolean(value: unknown): boolean {
    return value === true || value === 'true' || value === 1;
  }

  private stringifyValue(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return this.getEmptyText();
    }
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return String(value);
      }
    }
    return String(value);
  }

  private getNumberFormatter(): Intl.NumberFormat {
    const locale = this.getCurrentLocale();
    let formatter = this.numberFormatterCache.get(locale);
    if (!formatter) {
      formatter = new Intl.NumberFormat(locale);
      this.numberFormatterCache.set(locale, formatter);
    }
    return formatter;
  }

  private getDateTimeFormatter(): Intl.DateTimeFormat {
    const locale = this.getCurrentLocale();
    let formatter = this.dateTimeFormatterCache.get(locale);
    if (!formatter) {
      formatter = new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
      this.dateTimeFormatterCache.set(locale, formatter);
    }
    return formatter;
  }

  private getCurrentLocale(): string {
    if (this.i18n.currentLang === 'en-US') {
      return 'en-US';
    }
    if (this.i18n.currentLang === 'zh-TW') {
      return 'zh-TW';
    }
    return 'zh-CN';
  }

  private getEmptyText(): string {
    return this.t('common.none');
  }
}
